import { FormComponent } from './types';

/**
As of version 4 the default sort order of enums is the order in which they are defined.
https://github.com/UselessPickles/ts-enum-util/issues/7#issuecomment-475499821
*/
export enum ValidatorNames {
  required = 'required',
  max = 'max',
  maxlength = 'maxlength',
  min = 'min',
  minlength = 'minlength',
  pattern = 'pattern',
}
export interface Validator<T> {
  errorMessage?: string;
  validate: (htmlInput: T) => boolean;
}
export interface ValidatorEntry {
  name: string;
  customErrorMessage?: string;
  options?: boolean | string;
}

export const defaultErrorMessagesConfig: { [k in ValidatorNames]?: (options?: ValidatorEntry['options']) => string } = {
  required: () => 'This entry is required',
  maxlength: (options: ValidatorEntry['options']) => `Please enter a value that is ${options} characters or less`,
  minlength: (options: ValidatorEntry['options']) => `Please enter a value that is ${options} characters or more`,
  pattern: () => 'Please enter a value in the correct format',
};

/**  reducer callback: iterates through validators and returns the first one that fails it's validate function  */
export const combineValidators = <A>(v1: Validator<A>, v2: Validator<A>): Validator<A> => {
  const combined: Validator<A> = {
    validate: (vParam: A) => {
      const result1: boolean = v1.validate(vParam);
      const result2: boolean = v2.validate(vParam);

      if (!result1) combined.errorMessage = v1.errorMessage;
      else if (!result2) combined.errorMessage = v2.errorMessage;

      return result1 && result2;
    },
  };

  return combined;
};

/** creates a list of ValidatorEntry objects from the ValidatorNames enum that exist on the component class
    example: [
      {name: 'required},
      {name: 'min', options: '5'}
    ]
  */
export const composeValidatorList = (componentClass: FormComponent) =>
  Object.values(ValidatorNames)
    .filter((vname) => componentClass[vname] != null)
    .map(
      (validatorEntry): ValidatorEntry => ({
        name: validatorEntry,
        customErrorMessage: (componentClass as any)[`${validatorEntry}Message`],
        options: componentClass[validatorEntry],
      })
    );

export const defaultValidator = (): Validator<HTMLInputElement> => ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate: (_value?: any) => true,
});

export const getValidator = (list: Array<ValidatorEntry>): Validator<HTMLInputElement> =>
  (list || [])
    .map((validatorEntry) => validatorFactory(validatorEntry as ValidatorEntry))
    .reduce(combineValidators, defaultValidator());

export const initializeValidators = (componentClass: FormComponent) => {
  const validatorList = composeValidatorList(componentClass);
  return getValidator(validatorList);
};

export const isRequiredValidator = (customErrorMessage?: string): Validator<HTMLInputElement> => ({
  errorMessage: customErrorMessage || defaultErrorMessagesConfig.required(),
  validate: (inputElement: HTMLInputElement) =>
    inputElement && 'validity' in inputElement ? !inputElement.validity.valueMissing : true,
});

export const maxlengthValidator = (
  customErrorMessage: string,
  options: ValidatorEntry['options']
): Validator<HTMLInputElement> => ({
  errorMessage: customErrorMessage || defaultErrorMessagesConfig.maxlength(options),
  validate: (inputElement: HTMLInputElement) =>
    inputElement && 'validity' in inputElement ? !inputElement.validity.tooLong : true,
});

export const minlengthValidator = (
  customErrorMessage: string,
  options: ValidatorEntry['options']
): Validator<HTMLInputElement> => ({
  errorMessage: customErrorMessage || defaultErrorMessagesConfig.minlength(options),
  validate: (inputElement: HTMLInputElement) =>
    inputElement && 'validity' in inputElement ? !inputElement.validity.tooShort : true,
});

export const patternValidator = (customErrorMessage: string): Validator<HTMLInputElement> => ({
  errorMessage: customErrorMessage || defaultErrorMessagesConfig.pattern(),
  validate: (inputElement: HTMLInputElement) =>
    inputElement && 'validity' in inputElement ? !inputElement.validity.patternMismatch : true,
});

export const validatorFactory = (validatorEntry: ValidatorEntry) => {
  const { name, customErrorMessage, options } = validatorEntry;
  const { maxlength, minlength, pattern, required } = ValidatorNames;
  switch (name) {
    case maxlength:
      return maxlengthValidator(customErrorMessage, options);
    case minlength:
      return minlengthValidator(customErrorMessage, options);
    case pattern:
      return patternValidator(customErrorMessage);
    case required:
      return isRequiredValidator(customErrorMessage);
    default:
      return defaultValidator();
  }
};
